import {regexMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
import _, {isEmpty} from 'lodash';
import React, {
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {useAddToHistory} from '../../components/PanelBank/search/useAddToSearchHistory';
import type {BaseSearchHistoryItem} from '../../components/PanelBank/types';
import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {isInJupyterNotebook} from '../../setup';
import {
  type PanelSearchInputSearchContext,
  useRecordPanelSearchInput,
} from '../../util/datadog/workspace/panelSearchInput';
import {useDebouncedMirror, useDebounceState} from '../../util/hooks';
import {searchRegexFromQuery} from '../../util/panelbank';
import {usePart} from '../views/hooks';

/** Debounce for local panel-bank filtering UI (highlighting, visibility, etc.). */
export const PANEL_SEARCH_UI_DEBOUNCE_MS = 100;

/**
 * Debounce before writing search terms to the view spec.
 * Intentionally longer than {@link PANEL_SEARCH_UI_DEBOUNCE_MS} so
 * workspace autosave is less frequent than local filtering
 */
export const PANEL_SEARCH_VIEW_SPEC_SAVE_DEBOUNCE_MS = 1000;

/**
 * Debounce before sending PanelGenHistoryKeys requests.
 */
export const PANEL_SEARCH_NETWORK_DEBOUNCE_MS = 500;

const PanelSearchInputContext = React.createContext<{
  searchQuery: string; // original query typed by user (not debounced)
  hideSearchQuery: string; // original hide regex query typed by user (not debounced)
  searchHistory: BaseSearchHistoryItem[];
  searchInputCallbackRef(node: HTMLInputElement | null): void;
  onChangeSearchQuery(
    newSearchQuery: string,
    searchContext?: PanelSearchInputSearchContext
  ): void;
  onChangeHideSearchQuery(newHideSearchQuery: string): void;
  onAddToHistory?(query: string): void;
}>({
  searchQuery: '',
  hideSearchQuery: '',
  searchInputCallbackRef: () => {},
  onChangeSearchQuery: () => {},
  onChangeHideSearchQuery: () => {},
  onAddToHistory: () => {},
  searchHistory: [],
});
PanelSearchInputContext.displayName = 'PanelSearchInputContext';

const PanelSearchDebouncedContext = React.createContext<{
  isSearching: boolean;
  debouncedSearchQuery: string;
  networkDebouncedSearchQuery: string;
  debouncedSearchRegex: RegExp | null;
  debouncedHideSearchRegex: RegExp | null;
  isHiding: boolean;
  getIsSearchMatch: (test: string) => boolean;
  makeHighlightSearchTitle?: (title: string) => ReactNode;
}>({
  isSearching: false,
  debouncedSearchQuery: '',
  networkDebouncedSearchQuery: '',
  debouncedSearchRegex: null,
  debouncedHideSearchRegex: null,
  isHiding: false,
  getIsSearchMatch: () => false,
});
PanelSearchDebouncedContext.displayName = 'PanelSearchDebouncedContext';

type PanelSearchContextProviderProps = {
  children: ReactNode;
};

const PanelSearchContextProviderComp: React.FC<
  PanelSearchContextProviderProps
> = ({children}) => {
  const {panelBankConfigRef, panelBankActions} = useWorkspaceRefsContext();
  const panelBankConfigPart = usePart(panelBankConfigRef);

  const savedSearch = panelBankConfigPart.settings.searchQuery ?? '';
  const savedHideSearch = panelBankConfigPart.settings.hideSearchQuery ?? '';
  const searchHistory = useMemo(
    () => panelBankConfigPart.settings.searchHistory ?? [],
    [panelBankConfigPart.settings.searchHistory]
  );
  const shouldAutoFocus = !isInJupyterNotebook();

  const onSaveSearch = useCallback(
    (searchQuery: string, hideSearchQuery: string) => {
      panelBankActions.setCurrentSearch(searchQuery, hideSearchQuery);
    },
    [panelBankActions]
  );

  const onAddToHistory = useAddToHistory(
    panelBankActions.updateSearchHistory,
    panelBankConfigPart.settings.searchHistory
  );

  const searchInputCallbackRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node != null && shouldAutoFocus) {
        node.focus();
      }
    },
    [shouldAutoFocus]
  );

  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebounceState(
    savedSearch,
    PANEL_SEARCH_UI_DEBOUNCE_MS
  );
  const [hideSearchQuery, debouncedHideSearchQuery, setHideSearchQuery] =
    useDebounceState(savedHideSearch, PANEL_SEARCH_UI_DEBOUNCE_MS);

  const networkDebouncedSearchQuery = useDebouncedMirror(
    searchQuery,
    PANEL_SEARCH_NETWORK_DEBOUNCE_MS
  );

  const debouncedSaveSearch = useMemo(
    () => _.debounce(onSaveSearch, PANEL_SEARCH_VIEW_SPEC_SAVE_DEBOUNCE_MS),
    [onSaveSearch]
  );
  const trimmedDebouncedSearchQuery = debouncedSearchQuery.trim();

  // if saved search query (from the spec) is updated from outside of this
  // component (e.g. via undo/redo actions), update local state accordingly
  useEffect(() => {
    setSearchQuery(savedSearch);
  }, [savedSearch, setSearchQuery]);
  useEffect(() => {
    setHideSearchQuery(savedHideSearch);
  }, [savedHideSearch, setHideSearchQuery]);

  const recordPanelSearchInput = useRecordPanelSearchInput({
    debounceMs: PANEL_SEARCH_UI_DEBOUNCE_MS,
    renderedSearchQuery: trimmedDebouncedSearchQuery,
  });

  const onChangeSearchQuery = useCallback(
    (newSearchQuery: string, searchContext?: PanelSearchInputSearchContext) => {
      // Start the perf measurement at the accepted input, before debounce,
      // filtering, render, and persistence work that contributes to user wait.
      recordPanelSearchInput(newSearchQuery, searchContext);
      setSearchQuery(newSearchQuery);
      debouncedSaveSearch(newSearchQuery, hideSearchQuery);
    },
    [
      hideSearchQuery,
      debouncedSaveSearch,
      recordPanelSearchInput,
      setSearchQuery,
    ]
  );

  const onChangeHideSearchQuery = useCallback(
    (newHideSearchQuery: string) => {
      setHideSearchQuery(newHideSearchQuery);
      debouncedSaveSearch(searchQuery, newHideSearchQuery);
    },
    [debouncedSaveSearch, searchQuery, setHideSearchQuery]
  );

  const inputState = useMemo(
    () => ({
      searchInputCallbackRef,
      searchQuery, // original query typed by user (not debounced). used for display.
      hideSearchQuery,
      onChangeSearchQuery,
      onChangeHideSearchQuery,
      onAddToHistory,
      searchHistory,
    }),
    [
      hideSearchQuery,
      searchInputCallbackRef,
      searchQuery,
      onChangeHideSearchQuery,
      onChangeSearchQuery,
      onAddToHistory,
      searchHistory,
    ]
  );
  const trimmedDebouncedHideSearchQuery = debouncedHideSearchQuery.trim();
  const isSearching = !isEmpty(trimmedDebouncedSearchQuery);
  const isHiding = !isEmpty(trimmedDebouncedHideSearchQuery);
  const debouncedSearchRegex = useMemo(
    () => searchRegexFromQuery(trimmedDebouncedSearchQuery),
    [trimmedDebouncedSearchQuery]
  );
  const debouncedHideSearchRegex = useMemo(
    () => searchRegexFromQuery(trimmedDebouncedHideSearchQuery),
    [trimmedDebouncedHideSearchQuery]
  );
  const getIsSearchMatch = useCallback(
    (testString: string) => {
      if (debouncedSearchRegex == null) {
        // no search query, everything matches
        return true;
      }
      return debouncedSearchRegex.test(testString);
    },
    [debouncedSearchRegex]
  );

  // This takes in a string (e.g. a section title, or panel title)
  // And returns jsx to display the string with the search match letters highlighted
  const makeHighlightSearchTitle = useCallback(
    (title: string): ReactNode =>
      isSearching ? regexMatchHighlight(title, debouncedSearchRegex) : title,
    [debouncedSearchRegex, isSearching]
  );

  const trimmedNetworkDebouncedSearchQuery = networkDebouncedSearchQuery.trim();

  const debouncedState = useMemo(
    () => ({
      debouncedSearchQuery: trimmedDebouncedSearchQuery, // changes a lot, maybe should have its own context
      networkDebouncedSearchQuery: trimmedNetworkDebouncedSearchQuery,
      debouncedSearchRegex,
      debouncedHideSearchRegex,
      isSearching,
      isHiding,
      makeHighlightSearchTitle,
      getIsSearchMatch,
    }),
    [
      debouncedHideSearchRegex,
      debouncedSearchRegex,
      getIsSearchMatch,
      isHiding,
      isSearching,
      makeHighlightSearchTitle,
      trimmedDebouncedSearchQuery,
      trimmedNetworkDebouncedSearchQuery,
    ]
  );

  return (
    <PanelSearchInputContext.Provider value={inputState}>
      <PanelSearchDebouncedContext.Provider value={debouncedState}>
        {children}
      </PanelSearchDebouncedContext.Provider>
    </PanelSearchInputContext.Provider>
  );
};

export const PanelSearchContextProvider = memo(PanelSearchContextProviderComp);

export function usePanelSearchDebouncedContext() {
  return useContext(PanelSearchDebouncedContext);
}

export function usePanelSearchInputContext() {
  return useContext(PanelSearchInputContext);
}
